import React, { useEffect, useRef, useState } from "react";
import api, { apiErr } from "../lib/api";
import { PageHeader, fmtNum } from "../components/Shared";
import { toast } from "sonner";
import {
  Upload, Download, Loader2, X, Trash2, ArrowUpFromLine, ArrowDownToLine, Info,
} from "lucide-react";

export default function Mutasi() {
  const [data, setData] = useState({ items: [], total: 0, total_keluar: 0, total_masuk: 0 });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importMode, setImportMode] = useState("replace");
  const [file, setFile] = useState(null);
  const [clearing, setClearing] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mutations");
      setData(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doImport = async () => {
    if (!file) { toast.error("Pilih file Excel dulu"); return; }
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/mutations/import?mode=${importMode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.imported} baris mutasi diimpor`);
      setShowImport(false);
      setFile(null);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const doTemplate = async () => {
    try {
      const res = await api.get("/mutations/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Template_Mutasi_SO.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengunduh template"); }
  };

  const doClear = async () => {
    if (!window.confirm("Hapus semua data mutasi? Selisih saldo akan kembali dihitung tanpa mutasi.")) return;
    setClearing(true);
    try {
      await api.delete("/mutations");
      toast.success("Data mutasi dikosongkan");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setClearing(false); }
  };

  return (
    <div className="pb-12">
      <PageHeader title="Mutasi Selama SO" subtitle="Barang keluar/masuk (penjualan, IGD, pasien pulang) selama stock opname berlangsung">
        <button onClick={doTemplate} data-testid="mutasi-template-btn" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
          <Download className="h-4 w-4" /> Template
        </button>
        <button onClick={() => setShowImport(true)} data-testid="mutasi-import-btn" className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-3.5 py-2 text-sm font-semibold text-white hover:bg-teal-800">
          <Upload className="h-4 w-4" /> Impor Excel
        </button>
      </PageHeader>

      <div className="px-4 sm:px-6 lg:px-8 pt-6 space-y-4">
        <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-xs text-sky-700">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Mutasi dicatat <b>total per obat</b> (per ID Barang). Rumus di menu Saldo Master jadi:{" "}
            <b>Selisih = Qty Fisik − (Qty Awal − Keluar + Masuk)</b>. Jadi barang yang keluar secara sah tidak dianggap selisih.
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wider">Item Bermutasi</div>
            <div className="mt-1 font-head text-2xl font-extrabold text-slate-900 tabular" data-testid="mutasi-count">{fmtNum(data.total)}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-1.5 text-xs text-red-600 uppercase tracking-wider"><ArrowUpFromLine className="h-3.5 w-3.5" /> Total Keluar</div>
            <div className="mt-1 font-head text-2xl font-extrabold text-red-600 tabular">{fmtNum(data.total_keluar)}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-1.5 text-xs text-emerald-600 uppercase tracking-wider"><ArrowDownToLine className="h-3.5 w-3.5" /> Total Masuk</div>
            <div className="mt-1 font-head text-2xl font-extrabold text-emerald-600 tabular">{fmtNum(data.total_masuk)}</div>
          </div>
        </div>

        {data.total > 0 && (
          <div className="flex justify-end">
            <button onClick={doClear} disabled={clearing} data-testid="mutasi-clear-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60">
              {clearing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Kosongkan Mutasi
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {["Nama Obat", "ID Barang", "Satuan", "Keluar", "Masuk"].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                ) : data.items.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-16 text-center">
                    <Upload className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Belum ada data mutasi. Unduh template lalu impor laporan Excel keluar/masuk.</p>
                  </td></tr>
                ) : data.items.map((m) => (
                  <tr key={m.id_barang} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid="mutasi-row">
                    <td className="px-4 py-2.5 font-medium text-slate-800 max-w-xs">{m.nama || "-"}</td>
                    <td className="px-4 py-2.5 font-mono-t text-xs text-slate-500">{m.id_barang}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 uppercase">{m.satuan_jual || "-"}</td>
                    <td className="px-4 py-2.5 text-right tabular font-mono-t text-red-600">{fmtNum(m.keluar)}</td>
                    <td className="px-4 py-2.5 text-right tabular font-mono-t text-emerald-600">{fmtNum(m.masuk)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4" onClick={() => setShowImport(false)}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6" onClick={(e) => e.stopPropagation()} data-testid="mutasi-import-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-bold text-slate-900">Impor Mutasi SO</h3>
              <button onClick={() => setShowImport(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="flex gap-2">
                {[["replace", "Ganti semua"], ["append", "Tambah/Perbarui"]].map(([k, label]) => (
                  <button key={k} onClick={() => setImportMode(k)} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold ${importMode === k ? "border-teal-600 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-600"}`}>{label}</button>
                ))}
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" data-testid="mutasi-file-input" onChange={(e) => setFile(e.target.files[0])} className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-teal-700 file:px-3 file:py-2 file:text-white file:text-sm file:font-medium hover:file:bg-teal-800" />
              <p className="text-xs text-slate-500">Kolom: <b>ID Barang, Nama, Satuan, Keluar, Masuk</b>. ID Barang harus cocok dengan Master Obat.</p>
              <button onClick={doImport} disabled={importing} data-testid="mutasi-import-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Impor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
